import { Moment } from "moment"
import * as enm from "lib/enum"
import * as util from "lib/util"
import * as timePeriod from "enums/timePeriod"
import * as date from "lib/date"
import dayjs, { Dayjs } from "dayjs"
import moment from "moment"

export interface RelativeDateFilter {
	unit: timePeriod.TimePeriod
	count: int
}

interface dateFilterMap {
	Relative: RelativeDateFilter
	Range: [Moment | Dayjs, Moment | Dayjs]
	SingleDate: Moment
	AllTime: undefined
}

// The main date filter enum
export class DateFilter extends enm.Variant<dateFilterMap> {
	static AllTime(): DateFilter {
		return new DateFilter("AllTime", undefined)
	}
	static SingleDate(date: Moment): DateFilter {
		return new DateFilter("SingleDate", date)
	}
	static Range(dates: [Moment | Dayjs, Moment | Dayjs]): DateFilter {
		const toMoment = (d: Moment | Dayjs): Moment =>
			moment.isMoment(d) ? d : util.Moment(d.toDate())

		return new DateFilter("Range", [toMoment(dates[0]), toMoment(dates[1])])
	}

	static Relative(data: RelativeDateFilter): DateFilter {
		return new DateFilter("Relative", data)
	}

	GetDatePickerValue(): [Dayjs | null, Dayjs | null] {
		return this.Match({
			Range: ([start, end]) => {
				// Convert moments to dayjs objects
				const startDayjs = start ? dayjs(start.toDate()) : null
				// Handle the optional end date
				const endDayjs = dayjs(end.toDate())
				return [startDayjs, endDayjs]
			},
			SingleDate: (date) => {
				// For single date, use the same date for both start and end
				const dateDayjs = dayjs(date.toDate())
				return [dateDayjs, dateDayjs]
			},
			_: () => [null, null] as [Dayjs | null, Dayjs | null], // Default case for AllTime or other states
		})
	}

	GetStartDate(): Moment {
		const now = util.Moment()

		return this.Match({
			Relative: ({ unit, count }) => {
				// First get current date at start of day
				// Then subtract the appropriate amount
				return unit.Match({
					Day: () => util.Moment(now).startOf("day").subtract(count, "days"),
					Week: () => util.Moment(now).startOf("day").subtract(count, "weeks"),
					Month: () => util.Moment(now).startOf("day").subtract(count, "months"),
					Year: () => util.Moment(now).startOf("day").subtract(count, "years"),
				})
			},
			Range: ([start]) => util.Moment(start).startOf("day"),
			SingleDate: (date) => util.Moment(date).startOf("day"),
			AllTime: () => util.Moment(new Date(0)),
		})
	}

	GetDateRange() {
		return {
			ToArr: (): [Moment, Moment] => {
				return [this.GetStartDate(), this.GetEndDate()]
			},
			ToStr(format = "MMM D, YYYY"): string {
				const [start, end] = this.ToArr()
				return date.FmtDateRange(start, end, format)
			},
		}
	}

	GetEndDate(): Moment {
		return this.Match({
			Range: ([_, end]) => util.Moment(end).endOf("day"),
			SingleDate: (date) => util.Moment(date).endOf("day"),
			_: () => util.Moment().endOf("day"),
		})
	}

	IsCustom(): bool {
		return this.Is("SingleDate") || this.Is("Range")
	}

	ToStr(format = "MMM D, YYYY"): string {
		return this.Match({
			Relative: ({ unit, count }) => unit.ToStr(count),
			Range: () => (this as DateFilter).GetDateRange().ToStr(format),
			SingleDate: (date) => util.Moment(date).format(format),
			AllTime: () => "All Time",
		})
	}

	Includes(date: Date): bool {
		const [start, end] = (this as DateFilter).GetDateRange().ToArr()
		return util.Moment(date).isBetween(start, end, "day", "[]")
	}
}

export const AllTime = DateFilter.AllTime
export const SingleDate = DateFilter.SingleDate
export const Range = DateFilter.Range
export const Relative = DateFilter.Relative

export type DateFilterTag = keyof dateFilterMap

export const DateFilterEnum = enm.New([
	DateFilter.AllTime(),
	DateFilter.SingleDate(util.Moment()), // Using current moment as a placeholder
	DateFilter.Range([util.Moment(), util.Moment()]), // Using current moments as placeholders
	DateFilter.Relative({ unit: timePeriod.TimePeriods.Parse("Day").Expect("Invalid"), count: 1 }),
])

export function IsDateFilterTag(s: string): s is DateFilterTag {
	return DateFilterEnum.Contains(s as DateFilterTag)
}

export function IsDateFilter(v: unknown): v is DateFilter {
	return v instanceof DateFilter
}
